import type { Patient } from '@/contexts/PatientContext';

// How a portal tab relates to the active-chart / household-aggregate model.
//   'rollup'     — true combined household data (no active chart needed); the
//                  server already scopes to every accessible chart. Aggregate
//                  view fetches the roll-up directly. (Dashboard, Appointments,
//                  Payments.)
//   'per-person' — clinical data for ONE chart at a time. Aggregate view is a
//                  person-picker that fetches NO clinical data until the user
//                  drills into a concrete chart. (Health, Medications,
//                  Paperwork/Forms, Screening, Session Summaries.)
//   'none'       — account/household-level, not chart-scoped: excluded from the
//                  aggregate/chip model entirely. (Settings, Messages, Care
//                  Team, Case.)
export type ChartScope = 'rollup' | 'per-person' | 'none';

// Safety-override access levels that block a chart entirely (custody/DV
// suspensions). Mirrors the backend RESTRICTED_ACCESS_LEVELS.
const RESTRICTED_ACCESS_LEVELS = new Set(['none', 'suspended']);

/** Baseline accessibility: a chart is reachable unless its relationship carries
 * a restricted (none/suspended) safety-override access level. The viewer's own
 * chart is always accessible. Per-surface predicates (e.g. the guardian-only
 * health gate) narrow this further. */
export function isChartAccessible(patient: Patient | null | undefined): boolean {
  if (!patient) return false;
  const rt = String((patient as { relationship_type?: unknown }).relationship_type || '')
    .toLowerCase();
  if (rt === '' || rt === 'self' || rt === 'primary') return true;
  const accessLevel = String(
    (patient as { access_level?: unknown }).access_level || '',
  ).toLowerCase();
  return !RESTRICTED_ACCESS_LEVELS.has(accessLevel);
}

/**
 * Decide the active chart when ENTERING a surface, given the charts eligible for
 * that surface. Aggregate-first, decided per surface:
 *   >=2 eligible  -> null (aggregate / household summary, no active chart)
 *   exactly 1     -> that chart (direct — no aggregate step)
 *   0 / 'none'    -> null (surface is not chart-scoped)
 */
export function initialActiveChart(scope: ChartScope, eligibleIds: string[]): string | null {
  if (scope === 'none') return null;
  return eligibleIds.length >= 2 ? null : (eligibleIds[0] ?? null);
}

/** Whether a surface offers an aggregate/household view at all (>=2 eligible
 * charts on a chart-scoped surface). Drives whether the persistent chip shows. */
export function surfaceHasAggregate(scope: ChartScope, eligibleIds: string[]): boolean {
  return scope !== 'none' && eligibleIds.length >= 2;
}

// Persistent chart-context chip state for the CURRENT surface:
//   'none'      — nothing to disambiguate (single eligible chart, or not
//                 chart-scoped): no chip at all.
//   'aggregate' — >=2 eligible and no chart drilled in: neutral
//                 "Household — all charts" chip.
//   'viewing'   — a concrete chart is active: color-coded "Viewing: <name>" chip.
export type ChipMode = 'none' | 'aggregate' | 'viewing';

export function chipMode(args: {
  scope: ChartScope;
  eligibleIds: string[];
  activePatientId: string | null;
}): ChipMode {
  if (!surfaceHasAggregate(args.scope, args.eligibleIds)) return 'none';
  return args.activePatientId ? 'viewing' : 'aggregate';
}
